<template>
  <div
    id="education-candidate"
    class="create-profile-step-container"
  >
    <step-title
      :title="title"
      :subtitle="subtitle"
      :icon="icon"
    />
    <section class="section no-pad-top no-pad-left-right">
      <h5 class="title is-5">
        Degrees Attained
      </h5>
      <div
        v-for="(degree, index) in degrees"
        :key="degree.key"
        class="card card-content degree-container"
      >
        <div class="field">
          <div class="control is-expanded">
            <small class="is-size-7">Pursuing&nbsp;&nbsp;</small>
            <div class="field is-inline">
              <input
                :id="`degreeSwitch ${index + 1}`"
                v-model="degree.complete"
                type="checkbox"
                :name="`degreeSwitch ${index + 1}`"
                class="switch is-outlined is-small"
                @click="toggleDegreeCompletion(index)"
              >
              <label :for="`degreeSwitch ${index + 1}`" />
            </div>
            <small class="is-size-7">Complete</small>
          </div>
        </div>
        <div class="field">
          <div class="control is-expanded">
            <label class="label">Institution</label>
            <input
              v-model="degree.institution"
              v-validate="`${degree.institution_is_undisclosed ? '' : 'required'}`"
              class="input"
              type="text"
              placeholder="Ex: Harvard"
              :disabled="degree.institution_is_undisclosed"
              data-vv-validate-on="change|input|blur"
              :name="`institution ${index + 1}`"
              @keyup="debouncedUniversitySearch($event, index)"
              @focus="setFocusedIndex(index)"
              @blur="inputIsFocused = false"
              @keyup.up="keyboardUp()"
              @keyup.down="keyboardDown()"
            >
            <ul
              v-if="universitiesAutoComplete[focusedIndex].length > 0"
              class="autocomplete"
            >
              <li
                v-for="(result, resultsIndex) in universitiesAutoComplete[index]"
                :key="result.key"
                :class="resultsIndex === highlightedUniversityIndex ? 'is-highlighted' : ''"
                @click="selectUniversityFromSearchResults({ institution: result, degree_type: degree.degree_type, major: degree.major, complete: degree.complete })"
                @mouseover="highlightUniversity(resultsIndex)"
              >
                {{ result }}
              </li>
            </ul>
          </div>
        </div>
        <div class="field">
          <div class="control is-expanded">
            <div class="field is-inline">
              <input
                :id="`degreeDisclosureSwitch ${index + 1}`"
                v-model="degree.institution_is_undisclosed"
                type="checkbox"
                :name="`degreeDisclosureSwitch ${index + 1}`"
                class="switch is-outlined is-small"
                @click="toggleDegreeDisclosure(index)"
              >
              <label :for="`degreeDisclosureSwitch ${index + 1}`" />
            </div>
            <small class="is-size-7">Do not disclose institution</small>
          </div>
        </div>
        <div class="field">
          <div class="control is-expanded">
            <label class="label">Degree Type</label>
            <div class="select is-fullwidth is-primary">
              <select
                v-model="degree.degree_type"
                v-validate="'required'"
                :name="`Degree Type ${index + 1}`"
                @change="updateDegrees()"
              >
                <option />
                <option>Associate's</option>
                <option>Bachelor's</option>
                <option>Master's</option>
                <option>Doctorate</option>
                <option>Other</option>
              </select>
            </div>
            <p
              v-show="errors.has(`Degree Type ${index + 1}`)"
              class="help is-danger"
            >
              {{ errors.first(`Degree Type ${index + 1}`) }}
            </p>
          </div>
        </div>
        <div class="field">
          <div class="control is-expanded">
            <label class="label">Major</label>
            <input
              v-model="degree.major"
              v-validate="'required'"
              class="input"
              type="text"
              :name="`Major ${index + 1}`"
              placeholder="Ex: Political Science"
              @keyup="updateDegrees()"
            >
            <p
              v-show="errors.has(`Major ${index + 1}`)"
              class="help is-danger"
            >
              {{ errors.first(`Major ${index + 1}`) }}
            </p>
          </div>
        </div>
        <div @click="removeDegree(index)">
          <i class="fas fa-times-circle" />
        </div>
      </div>
      <div
        v-if="isLastDegreeFilled"
        class="add-card has-text-primary"
        @click="addDegree"
      >
        <i class="fas fa-plus" />
        <small
          v-if="degrees.length > 0"
          class="has-text-weight-bold"
        >&nbsp;Add Another Degree</small>
        <small
          v-else
          class="has-text-weight-bold"
        >&nbsp;Add a Degree</small>
      </div>
    </section>
    <section class="section no-pad-top no-pad-left-right">
      <h5 class="title is-5">
        Certifications & Licenses
      </h5>
      <div
        v-for="(certification, index) in certifications"
        :key="certification.key"
        class="card card-content degree-container"
      >
        <div class="field">
          <div class="control is-expanded">
            <small class="is-size-7">Certification&nbsp;&nbsp;</small>
            <div class="field is-inline">
              <input
                :id="`certificationTypeSwitch ${index + 1}`"
                v-model="certification.type"
                type="checkbox"
                :name="`certificationTypeSwitch ${index + 1}`"
                class="switch is-outlined is-small"
                @click="toggleCertificationType(index)"
              >
              <label :for="`certificationTypeSwitch ${index + 1}`" />
            </div>
            <small class="is-size-7">License</small>
          </div>
        </div>
        <div class="field">
          <div class="control is-expanded">
            <label class="label">Institution</label>
            <input
              v-model="certification.institution"
              v-validate="'required'"
              class="input"
              type="text"
              :name="`Institution ${index + 1}`"
              :placeholder="!certification.type ? 'Ex: American BAR Association' : 'Ex: NASBA'"
              @keyup="updateCertifications()"
              @click="updateCertifications()"
            >
          </div>
        </div>
        <div class="field">
          <div class="control is-expanded">
            <label class="label">{{ !certification.type ? 'Certification Name' : 'License Name' }}</label>
            <input
              v-model="certification.certification_name"
              v-validate="'required'"
              class="input"
              type="text"
              :name="`Certification or License Name ${index + 1}`"
              :placeholder="!certification.type ? 'Ex: BAR Certification' : 'Ex: CPA License'"
              @keyup="updateCertifications()"
            >
          </div>
        </div>
        <div
          v-if="certification.type"
          class="field"
        >
          <div class="control is-expanded">
            <small class="is-size-7">Inactive&nbsp;&nbsp;</small>
            <div class="field is-inline">
              <input
                :id="`licenseActiveSwitch ${index + 1}`"
                v-model="certification.active"
                type="checkbox"
                :name="`licenseActiveSwitch ${index + 1}`"
                class="switch is-outlined is-small"
                @click="toggleLicenseActive(index)"
              >
              <label :for="`licenseActiveSwitch ${index + 1}`" />
            </div>
            <small class="is-size-7">Active</small>
          </div>
        </div>
        <div
          v-else
          class="field"
        >
          <div class="control is-expanded">
            <small class="is-size-7">Pursuing&nbsp;&nbsp;</small>
            <div class="field is-inline">
              <input
                :id="`certificationCompleteSwitch ${index + 1}`"
                v-model="certification.complete"
                type="checkbox"
                :name="`certificationCompleteSwitch ${index + 1}`"
                class="switch is-outlined is-small"
                @click="toggleCertificationCompletion(index)"
              >
              <label :for="`certificationCompleteSwitch ${index + 1}`" />
            </div>
            <small class="is-size-7">&nbsp;&nbsp;Complete</small>
          </div>
        </div>
        <div>
          <i
            class="fas fa-times-circle"
            @click="removeCertification(index)"
          />
        </div>
      </div>
      <div
        v-if="isLastCertificationFilled"
        class="add-card has-text-primary"
        @click="addCertification"
      >
        <i class="fas fa-plus" />
        <small
          v-if="certifications.length > 0"
          class="has-text-weight-bold"
        >&nbsp;Add Another Certiifcation or License</small>
        <small
          v-else
          class="has-text-weight-bold"
        >&nbsp;Add a Certification or License</small>
      </div>
    </section>
  </div>
</template>

<script>
import { debounce } from 'lodash-es';
import StepTitle from './StepTitle';

export default {
  name: 'EducationCandidate',
  components: {
    StepTitle
  },
  data () {
    return {
      title: 'Education',
      subtitle: 'Add your Degrees, Certifications, and Licenses',
      icon: 'fa-user-graduate',
      degrees: [],
      certifications: [],
      universitiesAutoComplete: [],
      highlightedUniversityIndex: [],
      focusedIndex: 0,
      autoCompleteIsLoading: []
    };
  },
  computed: {
    isLastDegreeFilled () {
      if (this.degrees.length > 0) {
        let lastIndex = this.degrees.length - 1;
        return (
          this.degrees[lastIndex].institution !== ''
          && this.degrees[lastIndex].degree_type !== undefined
          && this.degrees[lastIndex].degree_type !== ''
          && this.degrees[lastIndex].major !== ''
        );
      } else {
        return true;
      }
    },
    isLastCertificationFilled () {
      if (this.certifications.length > 0) {
        let lastIndex = this.certifications.length - 1;
        return (
          this.certifications[lastIndex].institution !== ''
          && this.certifications[lastIndex].certification_name !== ''
        );
      } else {
        return true;
      }
    },
    isFormValid () {
      return Object.keys(this.fields).every(key => this.fields[key].valid);
    }
  },
  created () {
    this.degrees = this.$store.state.user.profile.education.degrees;
    this.certifications = this.$store.getters.getCandidateCertifications;
    this.universitiesAutoComplete = this.degrees.map(a => []);
    this.autoCompleteIsLoading = this.degrees.map(a => false);
  },
  mounted () {
    this.checkForm();
  },
  updated () {
    this.checkForm();
  },
  methods: {
    addDegree () {
      this.degrees.push({
        institution: '',
        degree_type: '',
        major: '',
        complete: true,
        institution_is_undisclosed: false
      });
      this.universitiesAutoComplete.push([]);
      this.autoCompleteIsLoading.push(false);
      this.updateDegrees();
    },

    addCertification () {
      this.certifications.push({
        institution: '',
        certification_name: '',
        complete: true,
        active: true,
        type: false
      });
      this.updateCertifications();
    },

    toggleDegreeCompletion (index) {
      this.degrees[index].complete = !this.degrees[index].complete;
      this.updateDegrees();
    },

    toggleDegreeDisclosure (index) {
      this.$validator
        .validate()
        .then(result => {
          this.checkForm();
        })
        .catch(error => {
          console.log(error);
        });
      this.degrees[index].institution_is_undisclosed = !this.degrees[index]
        .institution_is_undisclosed;
      this.updateDegrees();
    },

    toggleCertificationType (index) {
      this.certifications[index].type = !this.certifications[index].type;
      this.updateCertifications();
    },

    toggleCertificationCompletion (index) {
      this.certifications[index].complete = !this.certifications[index]
        .complete;
      this.updateCertifications();
    },

    toggleLicenseActive (index) {
      this.certifications[index].active = !this.certifications[index].active;
      this.updateCertifications();
    },

    removeDegree (index) {
      if (index > -1) {
        this.degrees.splice(index, 1);
      }
      this.updateDegrees();
    },

    removeCertification (index) {
      if (index > -1) {
        this.certifications.splice(index, 1);
      }
      this.updateCertifications();
    },

    debouncedUniversitySearch: debounce(function ($event) {
      this.searchForUniversities($event);
    }, 200),

    searchForUniversities ($event) {
      this.checkForm();
      /* If user presses UP or DOWN keys, the search will NOT run.
            This prevents running searches when user is trying to navigate
            dropdown options. */
      if (
        $event.code === 'ArrowUp'
        || $event.code === 'ArrowDown'
        || $event.code === 'Enter'
      ) {
        return;
      }

      let query = this.degrees[this.focusedIndex].institution;

      if (query.length < 2) {
        return;
      }

      this.universitiesAutoComplete[this.focusedIndex] = [];
      this.universitiesAutoComplete[this.focusedIndex].push(query);

      this.$set(this.autoCompleteIsLoading, this.focusedIndex, true);
      /* We use the current query as the first result to allow the
            user to select their written option */
      this.$store.dispatch('getUniversities', query)
        .then(response => {
          // Here we concatenate the user's query with all other results.
          let universitiesResults = this.universitiesAutoComplete[this.focusedIndex].concat(response.data.data.university); // eslint-disable-line

          if (response.data.data.university.includes(query)) {
            universitiesResults.shift(); // Remove first entry (query) if it is a duplicate.
          }
          this.$set(
            this.universitiesAutoComplete,
            this.focusedIndex,
            universitiesResults
          );
          this.$set(this.autoCompleteIsLoading, this.focusedIndex, false);
          this.autoCompleteIsLoading[this.focusedIndex] = false;
        })
        .catch(error => console.log(error));
    },

    selectUniversityFromSearchResults (result) {
      this.$set(this.degrees, this.focusedIndex, result);
      this.$set(this.universitiesAutoComplete, this.focusedIndex, []);
      this.highlightedUniversityIndex = 0;
      this.updateDegrees();
    },

    highlightUniversity (index) {
      this.highlightedUniversityIndex = index;
    },

    keyboardDown () {
      this.highlightedUniversityIndex++;
    },

    keyboardUp () {
      this.highlightedUniversityIndex--;
    },

    setFocusedIndex (index) {
      this.focusedIndex = index;
    },

    updateCertifications () {
      this.checkForm();
      this.$store.commit('EDIT_CANDIDATE_CERTIFICATIONS', this.certifications);
    },

    updateDegrees () {
      this.checkForm();
      this.$store.commit('EDIT_CANDIDATE_DEGREES', this.degrees);
    },

    checkForm () {
      setTimeout(() => {
        this.$emit('checkForm', this.isFormValid);
      }, 50);
    }
  }
};
</script>

<style lang="scss" scoped>
.degree-container {
  box-shadow: 0 2px 6px 0 rgba(77, 101, 117, 0.25);
  margin-bottom: 1rem;
}

.card {
  border-radius: 4px;
}

.add-card {
  cursor: pointer;
}

.fa-times-circle {
  position: absolute;
  right: -6px;
  top: -6px;
  cursor: pointer;
}

select:invalid {
  border-color: hsl(0, 0%, 86%);
}

.autocomplete {
  position: absolute;
  z-index: 10;
  background-color: white;
  width: 100%;
  max-height: 150px;
  overflow: scroll;
  border: 1px solid $primary;
  border-top: none;
  margin-top: -1px;
  transition: all 0.2s ease;
}

.is-highlighted {
  background-color: $primary;
  color: white;
  transition: all 0.2s ease;
}

li {
  padding: 0 0.5rem;
  cursor: pointer;
}
</style>
